import { CHATWOOT_BASE, authHeaders } from '@/api/chatwoot'
import { N8N_TEAMS_URL } from '@/api/n8n'

const ACCOUNT_PATH = `${CHATWOOT_BASE}/api/v1/accounts/3`

export interface TeamItem {
  id: number
  name: string
}

export interface TeamMemberItem {
  id: number
  name: string
  thumbnail: string
  email: string
}

/** Time completo, como usado na tela de gestão de times (CRUD) — diferente do TeamItem enxuto usado nos pickers do chat. */
export interface Team {
  id: number
  name: string
  description: string
  allow_auto_assign: boolean
  account_id: number
  is_member: boolean
}

/** Vínculo de um time a um fluxo/unidade ISBET (via n8n). */
export interface TeamFluxoInfo {
  fluxoId: number
  unidadeId: number
  nmUnidade: string | null
  cdUf: string | null
}

/** Lista todos os times da conta (para pickers de atribuição). */
export async function listTeams(token: string): Promise<TeamItem[]> {
  const res = await fetch(`${ACCOUNT_PATH}/teams`, { headers: authHeaders(token) })
  if (!res.ok) return []
  const data = await res.json()
  const list = Array.isArray(data) ? data : (data.payload || [])
  return list.map((t: any) => ({ id: t.id, name: t.name }))
}

/** Lista todos os times da conta com os dados completos (tela de gestão de times). */
export async function listTeamsFull(token: string): Promise<Team[]> {
  const res = await fetch(`${ACCOUNT_PATH}/teams`, { headers: authHeaders(token) })
  if (!res.ok) throw new Error(`Erro ${res.status}`)
  const data = await res.json()
  return Array.isArray(data) ? data : (data.payload ?? [])
}

/** Exclui um time. */
export async function deleteTeam(token: string, teamId: number): Promise<boolean> {
  const res = await fetch(`${ACCOUNT_PATH}/teams/${teamId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  return res.ok
}

/**
 * Lista os agentes atribuíveis a uma conversa: membros do time (se `teamId`
 * informado) ou todos os agentes da conta (fallback quando a conversa não
 * tem time). Usado pelos pickers de "atribuir agente" do chat.
 */
export async function listTeamMembersOrAgents(token: string, teamId: number | null): Promise<TeamMemberItem[]> {
  const url = teamId
    ? `${ACCOUNT_PATH}/teams/${teamId}/team_members`
    : `${ACCOUNT_PATH}/agents`
  const res = await fetch(url, { headers: authHeaders(token) })
  if (!res.ok) return []
  const data = await res.json()
  const list = Array.isArray(data) ? data : (data.payload || [])
  return list.map((a: any) => ({
    id: a.id,
    name: a.name || a.available_name || '—',
    thumbnail: a.thumbnail || '',
    email: a.email || '',
  }))
}

/** Remove um agente de um time. */
export async function removeTeamMember(token: string, teamId: number, agentId: number): Promise<boolean> {
  const res = await fetch(`${ACCOUNT_PATH}/teams/${teamId}/team_members`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ user_ids: [agentId] }),
  })
  return res.ok
}

/** Adiciona um ou mais agentes já existentes a um time. */
export async function addTeamMembers(token: string, teamId: number, agentIds: number[]): Promise<boolean> {
  const res = await fetch(`${ACCOUNT_PATH}/teams/${teamId}/team_members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ user_ids: agentIds }),
  })
  return res.ok
}

export interface NewAgentPayload {
  name: string
  email: string
  role: string
}

/** Cria um agente novo na conta. Lança erro com a mensagem do Chatwoot em caso de falha. */
export async function createAgent(token: string, agent: NewAgentPayload): Promise<TeamMemberItem> {
  const res = await fetch(`${ACCOUNT_PATH}/agents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(agent),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err?.message || `Erro ${res.status}`)
  }
  const created = await res.json()
  return { id: created.id, name: created.name || '—', thumbnail: created.thumbnail || '', email: created.email || '' }
}

export interface TeamFormPayload {
  name: string
  description: string
  allow_auto_assign: boolean
}

/** Cria um time novo no Chatwoot. */
export async function createTeam(token: string, payload: TeamFormPayload): Promise<Team> {
  const res = await fetch(`${ACCOUNT_PATH}/teams`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(`Erro ${res.status}`)
  return res.json()
}

/** Atualiza um time existente (edição, ou vínculo de um time já existente do Chatwoot). */
export async function updateTeam(token: string, teamId: number, payload: TeamFormPayload): Promise<Team> {
  const res = await fetch(`${ACCOUNT_PATH}/teams/${teamId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(`Erro ${res.status}`)
  return res.json()
}

/** Busca os vínculos de fluxo/unidade ISBET de um time (via n8n). */
export async function fetchTeamFluxoInfo(teamId: number): Promise<TeamFluxoInfo[]> {
  const res = await fetch(N8N_TEAMS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ command: 'ge_time_chat', chatwoot_id: teamId }),
  })
  const data = await res.json()
  const associations: Array<{ idFluxo: number; idUnidade: number; idChatwootTeam: number; unidadeIsbet?: string | null }> =
    Array.isArray(data) ? data : []
  return associations.map(a => ({
    fluxoId:   a.idFluxo,
    unidadeId: a.idUnidade,
    nmUnidade: a.unidadeIsbet ?? null,
    cdUf:      null,
  }))
}

export interface UnidadeIsbet {
  id_unidade: number
  municipio: string
}

/** Lista as unidades ISBET de um estado (UF), via n8n. */
export async function fetchUnidadesByUF(uf: string): Promise<UnidadeIsbet[]> {
  const res = await fetch(N8N_TEAMS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ command: 'get_unidades', uf }),
  })
  const data = await res.json()
  const arr  = Array.isArray(data) ? data : [data]
  return arr[0]?.obj?.unidades ?? []
}

/** Vincula um time a uma unidade/fluxo ISBET (via n8n). */
export async function linkTeamToUnidade(
  teamId: number,
  fluxoId: number,
  unidadeId: number,
  unidadeNome: string | null,
): Promise<void> {
  await fetch(N8N_TEAMS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      command:          'set_unidade_fluxo',
      fluxo_id:         fluxoId,
      team_chatwoot_id: teamId,
      unidade_id:       unidadeId,
      unidade_isbet:    unidadeNome,
    }),
  })
}
